import React, { useEffect } from 'react';
import { FormInputSelect } from 'components/form';
import { formatCurrency } from 'utils';
import { ProductConfig } from '../../../../../types';
import { BuckeyePlan, BuckeyeTerm } from './types';

interface Props {
  plans: BuckeyePlan[];
  configuration: ProductConfig;
  handlePlanChange: (index: number) => void;
  handleTermChange: (term: BuckeyeTerm) => void;
  handleDeductibleChange: (deductibleId: string) => void;
}

export const Fields = ({ plans, configuration, handlePlanChange, handleTermChange, handleDeductibleChange }: Props) => {
  const selectedPlan = configuration.buckeyePlanIndex != null ? (plans[configuration.buckeyePlanIndex] ?? null) : null;

  const selectedTerm: BuckeyeTerm | null = configuration.coverage ?? null;
  const sortedTerms = selectedPlan ? [...selectedPlan.terms].sort((a, b) => a.months - b.months) : [];

  useEffect(() => {
    if (plans?.length === 1 && configuration.buckeyePlanIndex == null) {
      handlePlanChange(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  useEffect(() => {
    if (selectedTerm?.deductibles?.length === 1 && !configuration.buckeyeDeductibleId) {
      handleDeductibleChange(selectedTerm.deductibles[0].deductibleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  return (
    <>
      <FormInputSelect
        variant="standard"
        label="Plan"
        onChange={(value: string) => handlePlanChange(parseInt(value, 10))}
        value={configuration.buckeyePlanIndex != null ? configuration.buckeyePlanIndex.toString() : ''}
        options={[
          { label: 'Select Plan', value: '' },
          ...plans.map((plan, index) => ({
            label: plan.name,
            value: index.toString(),
          })),
        ]}
      />

      {selectedPlan && (
        <FormInputSelect
          variant="standard"
          label="Term"
          onChange={(value: string) => {
            const term = sortedTerms[parseInt(value, 10)];
            if (term) handleTermChange(term);
          }}
          value={selectedTerm ? sortedTerms.findIndex(t => t.rateId === selectedTerm.rateId).toString() : ''}
          options={[
            { label: 'Select Term', value: '' },
            ...sortedTerms.map((term, index) => ({
              label: `${term.months} Months ${term.miles ? `/ ${term.miles.toLocaleString()} Miles` : ''} \u2014 ${formatCurrency(
                term.pricing.retailAmount
              )}`,
              value: index.toString(),
            })),
          ]}
        />
      )}

      {selectedTerm && selectedTerm.deductibles.length > 0 && (
        <FormInputSelect
          variant="standard"
          label="Deductible"
          onChange={handleDeductibleChange}
          value={configuration.buckeyeDeductibleId ?? ''}
          options={[
            { label: 'Select Deductible', value: '' },
            ...selectedTerm.deductibles.map(deductible => ({
              label: deductible.description,
              value: deductible.deductibleId,
            })),
          ]}
        />
      )}
    </>
  );
};
